import { Controller } from 'react-hook-form'
import { FileSignature, Save, RotateCcw } from 'lucide-react'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import companyFormStyles from '../../styles/organizationCard.module.css'
import type { OrganizationEditSectionProps } from '../../types/components'
import { ORG_FORM_SUBHEADING, CERTIFICATE_CONTRACT_VARIABLES } from '../../constants/editPage'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'

export function OrganizationCertificateTab({ ctx }: OrganizationEditSectionProps) {
  if (ctx.orgId !== 1) return null

  const hasDraft = Boolean(ctx.certificateTemplateDraft)

  return (
    <div
      id="org-panel-certificate"
      role="tabpanel"
      aria-labelledby="org-tab-certificate"
      hidden={ctx.selectedTab !== 'certificate'}
    >
      <div className="space-y-6">
        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <FileSignature className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              Contract {hasDraft ? '(In Draft)' : ''}
            </h2>
          </div>

          <div className={companyFormStyles.cardBody}>
            <div className="space-y-6">
              <div>
                <p className={ORG_FORM_SUBHEADING} id="org-certificate-form-variables-heading">
                  Available Variables:
                </p>
                <div
                  className={companyFormStyles.variablesBox}
                  aria-labelledby="org-certificate-form-variables-heading"
                  role="list"
                  aria-label="Certificate contract template variables"
                >
                  <div className="flex flex-wrap gap-x-2 gap-y-2">
                    {CERTIFICATE_CONTRACT_VARIABLES.map((v) => (
                      <code key={v} className={companyFormStyles.variableCode}>
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              </div>

              <div className={companyFormStyles.formGroup}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <label className={companyFormStyles.formLabel} htmlFor="org-certificate-template">
                    Contract Template
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={!ctx.isCertificateDirty || ctx.draftSaving}
                      onClick={() => void ctx.updateCertificateDraft(false)}
                      className={cn(primaryEntityActionButtonClass, 'px-3 py-1.5 text-xs flex items-center gap-1.5')}
                      title="Save Draft"
                    >
                      <Save className="h-3.5 w-3.5" />
                      Save Draft
                    </button>
                    <button
                      type="button"
                      disabled={!hasDraft || ctx.draftSaving}
                      onClick={() => void ctx.updateCertificateDraft(true)}
                      className={cn(secondaryEntityActionButtonClass, 'px-3 py-1.5 text-xs flex items-center gap-1.5 text-red-600 hover:text-red-700')}
                      title="Discard Draft"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Discard Draft
                    </button>
                  </div>
                </div>
                <Controller
                  name="certificate_template"
                  control={ctx.control}
                  render={({ field: { value, onChange } }) => (
                    <CKEditor4 id="org-certificate-template" value={value || ''} onChange={onChange} />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
